import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { Fragment } from 'react';
import { useNavigate } from 'react-router';

import { ChangeOrganizationModal } from '../ChangeOrganizationModal';
import { Avatar } from '../primitives/Avatar';
import { useShouldShowOrgSettings } from './useShouldShowOrgSettings';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { authSlice } from 'src/features/auth/auth.slice';
import { authService } from 'src/features/auth/authService';
import { selectCurrentOrganization } from 'src/features/auth/selectors';
import { useUserRoles } from 'src/features/auth/useUserRoles';
import { useCanViewDeveloperPlatform } from 'src/features/developer-platform/useCanViewDeveloperPlatform';
import { useDeveloperPlatformAnnouncement } from 'src/features/developer-platform/useDeveloperPlatformAnnouncement';

export function ProfileDropdown() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isBambi } = useUserRoles();

  const currentOrganization = useAppSelector(selectCurrentOrganization);
  const user = useAppSelector((state) => state.auth.profile);
  const shouldShowSettings = useShouldShowOrgSettings();
  const shouldShowDeveloperPlatform = useCanViewDeveloperPlatform();
  const { isNew: isDeveloperPlatformNew, markSeen: markDeveloperPlatformSeen } =
    useDeveloperPlatformAnnouncement();

  const showNewIndicator =
    shouldShowDeveloperPlatform && isDeveloperPlatformNew;

  if (!user) {
    throw new Error('User is required for ProfileDropdown');
  }

  const { first_name, last_name } = user;

  return (
    <Menu as="div" className="relative border-l">
      {showNewIndicator && <NewFlag />}
      <Menu.Button data-testid="profile-drop-down-btn">
        <div className="hidden items-center gap-4 truncate pl-6 md:flex">
          <div className="flex max-w-[125px] flex-col text-left md:max-w-[175px]">
            <div
              className="truncate text-sm font-semibold"
              title={currentOrganization?.name || ''}
            >
              {currentOrganization?.name || ''}
            </div>
            <div className="truncate text-xs text-gray-700">
              {first_name} {last_name}
            </div>
          </div>
          <div>
            <ChevronDownIcon className="h-4 w-4 text-black" />
          </div>
        </div>
        <div className="flex items-center gap-4 pl-4 md:hidden">
          <div className="relative">
            <Avatar firstName={first_name} lastName={last_name} />
            {showNewIndicator && (
              <span
                data-testid="profile-drop-down-new-dot"
                aria-label="Something new is available"
                className="absolute -right-0.5 -top-0.5 block h-2.5 w-2.5 rounded-full bg-mint ring-2 ring-white"
              />
            )}
          </div>
        </div>

        <span className="sr-only">Open user menu</span>
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {shouldShowSettings && (
            <ProfileDropdownMenuItem>
              <button
                onClick={() => {
                  navigate('/settings');
                }}
                className={
                  'block w-full px-4 py-2 text-left text-sm text-gray-700'
                }
              >
                Settings
              </button>
            </ProfileDropdownMenuItem>
          )}
          {shouldShowDeveloperPlatform && (
            <ProfileDropdownMenuItem>
              <button
                data-testid="developer-platform-menu-item"
                onClick={() => {
                  markDeveloperPlatformSeen();
                  navigate('/developer');
                }}
                className={
                  'flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm text-gray-700'
                }
              >
                Developer Platform
                {isDeveloperPlatformNew && (
                  <span
                    data-testid="developer-platform-new-badge"
                    className="rounded-full bg-mint px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                  >
                    New
                  </span>
                )}
              </button>
            </ProfileDropdownMenuItem>
          )}
          {user.memberships.length > 1 && (
            <ProfileDropdownMenuItem>
              <button
                onClick={() => {
                  dispatch(authSlice.actions.setIsOrganizationChangeOpen(true));
                }}
                className={
                  'block w-full px-4 py-2 text-left text-sm text-gray-700'
                }
              >
                Change Organization
              </button>
            </ProfileDropdownMenuItem>
          )}
          {isBambi && (
            <ProfileDropdownMenuItem>
              <button
                className={
                  'block w-full px-4 py-2 text-left text-sm text-gray-700'
                }
                onClick={() => navigate('/admin')}
              >
                Admin
              </button>
            </ProfileDropdownMenuItem>
          )}
          <ProfileDropdownMenuItem>
            <button
              onClick={authService.signOut}
              className={
                'block w-full px-4 py-2 text-left text-sm text-gray-700'
              }
            >
              Sign out
            </button>
          </ProfileDropdownMenuItem>
        </Menu.Items>
      </Transition>
      <ChangeOrganizationModal />
    </Menu>
  );
}

// Absolute so it costs the trigger no width. It clears the organization name
// to its left and the chevron below it, sitting in the corner the h-16 bar
// leaves free.
function NewFlag() {
  return (
    <span
      data-testid="profile-drop-down-new-flag"
      aria-label="Something new is available"
      className="pointer-events-none absolute -right-2 -top-2 z-10 hidden rounded-full bg-mint px-1.5 py-px text-[10px] font-bold uppercase leading-tight tracking-wide text-white shadow-sm ring-1 ring-white md:block"
    >
      New
    </span>
  );
}

function ProfileDropdownMenuItem({ children }: { children: React.ReactNode }) {
  return (
    <Menu.Item>
      <div className="w-full text-left hover:bg-mint-light hover:text-indigo">
        {children}
      </div>
    </Menu.Item>
  );
}
